/**
 * Business Subscription Page
 * 업체 구독 관리 페이지
 */

import { PageContainer } from "@/components/layout/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle, Gem, Loader2, XCircle } from "lucide-react";
import { Link, useRoute } from "wouter";
import { useEffect, useState } from "react";
import { DEFAULT_BUSINESS_PREMIUM_COPY, type BusinessPremiumFeature } from "@shared/business-premium";

interface SubscriptionInfo {
  business_id: string;
  subscription_status: string;
  subscription_expires_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_subscription: {
    id: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
  } | null;
  subscriptions_enabled: boolean;
  plans: Array<{
    code: "premium";
    billing_period: "monthly" | "yearly";
    display_name: string;
    price_cents: number;
    currency: string;
    trial_days: number;
  }>;
  content?: {
    plan_name: string;
    headline: string;
    description: string;
    cta_text: string;
    features: BusinessPremiumFeature[];
  };
  promotion?: {
    code: string;
    display_name: string;
    discount_percent: number;
    duration_months: number;
    billing_period: "monthly";
    starts_at: string;
    ends_at: string;
    discounted_price_cents: number;
  } | null;
  entitlements?: {
    temporaryFullAccess: boolean;
    subscriptionNoticeAt: string | null;
    subscriptionEnforcementAt: string | null;
  };
}

type ApiEnvelope<T> = { ok: true; data: T } | { ok: false; error?: { message?: string } };

async function readApi<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiEnvelope<T> | T;
  if (!res.ok || (typeof body === "object" && body !== null && "ok" in body && body.ok === false)) {
    const message =
      typeof body === "object" && body !== null && "error" in body
        ? body.error?.message
        : undefined;
    throw new Error(message || "요청을 처리하지 못했습니다");
  }
  if (typeof body === "object" && body !== null && "ok" in body && body.ok === true) {
    return body.data;
  }
  return body as T;
}

function BusinessSubscription() {
  const [match, params] = useRoute<{ id: string }>(
    "/businesses/:id/subscription"
  );
  const { user } = useAuth();
  const { toast } = useToast();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("yearly");

  if (!match || !params) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">유효하지 않은 업체 ID입니다</p>
            <Link href="/business-directory">
              <Button className="mt-4">목록으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const businessId = params.id;

  // Fetch subscription info
  const { data: subscription, isLoading } = useQuery<SubscriptionInfo>({
    queryKey: ["/api/businesses", businessId, "subscription"],
    queryFn: async () => {
      const token = await user?.getIdToken();
      const res = await fetch(`/api/businesses/${businessId}/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return readApi<SubscriptionInfo>(res);
    },
    enabled: !!user && !!businessId,
  });

  // Upgrade mutation
  const upgradeMutation = useMutation({
    mutationFn: async ({
      billingPeriod,
    }: {
      billingPeriod: "monthly" | "yearly";
    }) => {
      const token = await user?.getIdToken();
      const res = await fetch(`/api/businesses/${businessId}/subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tier: "premium", billing_period: billingPeriod }),
      });
      return readApi<{ checkout_url: string; session_id: string }>(res);
    },
    onSuccess: (data) => {
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "업그레이드 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const token = await user?.getIdToken();
      const res = await fetch(`/api/businesses/${businessId}/subscription`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return readApi<{ success: boolean; message: string }>(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/businesses", businessId, "subscription"],
      });
      toast({
        title: "구독이 취소되었습니다",
        description: "현재 기간 종료 시 구독이 취소됩니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "취소 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    const checkout = new URLSearchParams(window.location.search).get("checkout");
    if (checkout === "success") {
      toast({
        title: "결제가 접수되었습니다",
        description: "Stripe 확인 후 구독 상태가 자동으로 갱신됩니다.",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/businesses", businessId, "subscription"],
      });
    } else if (checkout === "cancelled") {
      toast({ title: "결제가 취소되었습니다", description: "요금이 청구되지 않았습니다." });
    }
  }, [businessId, toast]);

  if (isLoading) {
    return (
      <PageContainer size="read">
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  const currentTier = subscription?.subscription_status || "free";
  const isPremium = currentTier === "premium" || currentTier === "platinum";
  const subscriptionsEnabled = subscription?.subscriptions_enabled === true;
  const getPrice = () => {
    const configured = subscription?.plans?.find(
      (plan) => plan.billing_period === billingPeriod
    );
    return configured ? configured.price_cents / 100 : billingPeriod === "monthly" ? 19 : 190;
  };
  const content = subscription?.content || {
    plan_name: DEFAULT_BUSINESS_PREMIUM_COPY.name,
    headline: DEFAULT_BUSINESS_PREMIUM_COPY.headline,
    description: DEFAULT_BUSINESS_PREMIUM_COPY.description,
    cta_text: DEFAULT_BUSINESS_PREMIUM_COPY.cta,
    features: [...DEFAULT_BUSINESS_PREMIUM_COPY.features],
  };
  const promotion = billingPeriod === "monthly" ? subscription?.promotion : null;

  return (
    <PageContainer size="read">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">구독 관리</h1>
          <p className="text-muted-foreground">
            업체 구독을 관리하고 업그레이드하세요
          </p>
        </div>

        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-4 text-sm">
            <p className="font-semibold">
              {subscription?.entitlements?.temporaryFullAccess
                ? "안정화 기간에는 모든 업체 관리 기능이 무료입니다."
                : "기본 업체 정보와 Claim은 계속 무료입니다."}
            </p>
            <p className="mt-1 text-muted-foreground">
              {subscription?.entitlements?.temporaryFullAccess
                ? "유료 전환은 최소 30일 전에 공지하며, 현재는 결제 없이 전체 기능을 사용할 수 있습니다."
                : "유료 플랜은 추가 노출, 문의 관리와 측정 가능한 고객 행동 분석을 제공합니다."}
            </p>
          </CardContent>
        </Card>

        {/* Current Subscription */}
        <Card>
          <CardHeader>
            <CardTitle>현재 구독</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg font-semibold">
                    {isPremium ? "OZK Business Premium" : "Free"}
                  </span>
                  {isPremium && <Badge className="bg-blue-600">Premium</Badge>}
                </div>
                {subscription?.subscription_expires_at && (
                  <p className="text-sm text-muted-foreground">
                    만료일:{" "}
                    {new Date(
                      subscription.subscription_expires_at
                    ).toLocaleDateString("ko-KR")}
                  </p>
                )}
              </div>
              {isPremium ? (
                <Button
                  variant="outline"
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                >
                  {cancelMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      취소 중...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      구독 취소
                    </>
                  )}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Upgrade Plans */}
        {currentTier === "free" && (
          <div className="space-y-5">
            <div className="mx-auto flex w-fit rounded-lg border bg-muted/40 p-1">
              <Button
                type="button"
                size="sm"
                variant={billingPeriod === "monthly" ? "default" : "ghost"}
                onClick={() => setBillingPeriod("monthly")}
              >
                월간
              </Button>
              <Button
                type="button"
                size="sm"
                variant={billingPeriod === "yearly" ? "default" : "ghost"}
                onClick={() => setBillingPeriod("yearly")}
              >
                연간 · 2개월 무료
              </Button>
            </div>
            {!subscriptionsEnabled && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-center text-sm text-amber-900">
                결제와 성과 측정을 최종 검증 중입니다. 플랜 가격은 확정되었으며 구매는 검증 후 열립니다.
              </div>
            )}
            <Card className="mx-auto w-full max-w-2xl border-primary/40 shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Gem className="w-5 h-5 text-primary" />
                  <CardTitle>{content.plan_name}</CardTitle>
                  <Badge>통합 플랜</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div><h2 className="text-lg font-semibold">{content.headline}</h2><p className="mt-1 text-sm text-muted-foreground">{content.description}</p></div>
                <div>
                  {promotion ? <div className="flex items-end gap-3"><div className="text-3xl font-bold">A${(promotion.discounted_price_cents / 100).toFixed(2)}</div><div className="pb-1 text-muted-foreground line-through">A${getPrice().toFixed(2)}</div><Badge className="mb-1 bg-rose-600">{promotion.discount_percent}% 할인</Badge></div> : <div className="text-3xl font-bold">A${getPrice().toFixed(2)}</div>}
                  <div className="text-sm text-muted-foreground">
                    {billingPeriod === "monthly" ? "매월" : "매년"} · 추가 가입비 없음
                  </div>
                  {promotion && <p className="mt-1 text-sm font-medium text-rose-700">{promotion.display_name}: 첫 {promotion.duration_months}개월 적용 후 월 A${getPrice().toFixed(2)}</p>}
                </div>
                <Separator />
                <ul className="grid gap-3 sm:grid-cols-2">{content.features.map((feature) => <li key={feature.id} className="flex gap-2 text-sm"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /><span><strong className="block">{feature.title}</strong><span className="text-muted-foreground">{feature.description}</span></span></li>)}</ul>
                <Button
                  className="w-full"
                  onClick={() =>
                    upgradeMutation.mutate({
                      billingPeriod,
                    })
                  }
                  disabled={upgradeMutation.isPending || !subscriptionsEnabled}
                >
                  {upgradeMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      처리 중...
                    </>
                  ) : (
                    subscriptionsEnabled ? content.cta_text : "출시 준비 중"
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export default BusinessSubscription;
